import React, { useState } from 'react'
import { 
  CreditCard, 
  Package, 
  Calendar, 
  TrendingUp, 
  AlertCircle, 
  CheckCircle,
  ExternalLink,
  Settings,
  Download,
  RefreshCw,
  X,
  Zap,
  Crown
} from 'lucide-react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { motion, AnimatePresence } from 'framer-motion'
import toast from 'react-hot-toast'
import { apiService } from '../../services/api'
import LoadingSpinner from '../common/LoadingSpinner'
import { Button } from '../ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { cn } from '../../utils/cn'

const ClientBilling = () => {
  const [isUpgrading, setIsUpgrading] = useState(false)
  const [showUpgradeModal, setShowUpgradeModal] = useState(false)
  const [showCancelModal, setShowCancelModal] = useState(false)
  const [selectedPlan, setSelectedPlan] = useState(null)
  const queryClient = useQueryClient()

  // Fetch billing data
  const { data: billing, isLoading: billingLoading } = useQuery(
    'client-billing',
    () => apiService.get('/billing').then(res => res.data)
  )

  // Fetch client profile
  const { data: profile, isLoading: profileLoading } = useQuery(
    'client-profile',
    () => apiService.get('/clients/profile').then(res => res.data)
  )

  // Fetch available plans
  const { data: plans, isLoading: plansLoading } = useQuery(
    'billing-plans',
    () => apiService.get('/billing/plans').then(res => res.data)
  )

  // Fetch Stripe config
  const { data: stripeConfig } = useQuery(
    'stripe-config',
    () => apiService.get('/billing/config').then(res => res.data)
  )

  // Create checkout session mutation
  const createCheckoutMutation = useMutation(
    async (planId) => {
      const response = await apiService.post('/billing/create-checkout-session', { planId })
      return response.data
    },
    {
      onSuccess: (data) => {
        // Redirect to Stripe Checkout
        window.location.href = data.checkoutUrl
      },
      onError: (error) => {
        console.error('Checkout error:', error)
        toast.error('Failed to create checkout session. Please try again.')
      }
    }
  )

  // Cancel subscription mutation
  const cancelSubscriptionMutation = useMutation(
    () => apiService.post('/billing/cancel'),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('client-billing')
        queryClient.invalidateQueries('client-profile')
        toast.success('Subscription cancelled successfully!')
      },
      onError: (error) => {
        console.error('Cancel error:', error)
        toast.error('Failed to cancel subscription. Please try again.')
      }
    }
  )

  const handleUpgrade = (planId) => {
    setSelectedPlan({ id: planId, ...plans[planId] })
    setShowUpgradeModal(true)
  }

  const confirmUpgrade = () => {
    createCheckoutMutation.mutate(selectedPlan.id)
    setShowUpgradeModal(false)
  }

  const handleCancelSubscription = () => {
    setShowCancelModal(true)
  }

  const confirmCancel = () => {
    cancelSubscriptionMutation.mutate()
    setShowCancelModal(false)
  }

  if (billingLoading || profileLoading || plansLoading) {
    return (
      <Card className="border-neutral-200 dark:border-[#2f2b41]">
        <CardContent className="flex items-center justify-center py-12">
          <LoadingSpinner size="lg" text="Loading billing information..." />
        </CardContent>
      </Card>
    )
  }

  const currentPlan = profile?.subscription_plan || 'basic'
  const planData = plans?.[currentPlan]
  const isFreePlan = planData?.price === 0
  const isActive = profile?.billing_status === 'active' || isFreePlan
  const isCancelled = profile?.billing_status === 'canceled' || profile?.billing_status === 'cancelled'

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-neutral-900 dark:text-neutral-50">
          Billing & Subscription
        </h1>
        <p className="text-sm text-neutral-600 dark:text-neutral-400">
          Manage your subscription and billing information
        </p>
      </div>

      {/* Current Subscription */}
      <Card className="border-neutral-200 dark:border-[#2f2b41]">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Package className="w-5 h-5 text-accent-600 dark:text-accent-400" />
            <span>Current Subscription</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center space-x-3">
                <h3 className="text-2xl font-bold text-neutral-900 dark:text-neutral-50">
                  {planData?.name || 'Basic Plan'}
                </h3>
                <span className={cn(
                  "px-2 py-1 text-xs font-medium rounded-full",
                  isActive 
                    ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                    : "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
                )}>
                  {isActive ? 'Active' : profile?.billing_status || 'Inactive'}
                </span>
              </div>
              <p className="text-neutral-600 dark:text-neutral-400 mt-1">
                £{planData?.price ?? '24.99'}/month
              </p>
              <div className="mt-4 space-y-2">
                <div className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span className="text-sm text-neutral-600 dark:text-neutral-400">
                    {planData?.features?.sites || 3} websites
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span className="text-sm text-neutral-600 dark:text-neutral-400">
                    {planData?.features?.editsPerMonth === 'unlimited' 
                      ? 'Unlimited' 
                      : `${planData?.features?.editsPerMonth || 50}`} edits per month
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span className="text-sm text-neutral-600 dark:text-neutral-400">
                    {planData?.features?.storage || '1GB'} storage
                  </span>
                </div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-sm text-neutral-500 dark:text-neutral-400 mb-2">
                Next billing date
              </div>
              <div className="text-lg font-semibold text-neutral-900 dark:text-neutral-50">
                {profile?.quota_reset_date 
                  ? new Date(profile.quota_reset_date).toLocaleDateString()
                  : 'N/A'
                }
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Usage Overview */}
      <Card className="border-neutral-200 dark:border-[#2f2b41]">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <TrendingUp className="w-5 h-5 text-accent-600 dark:text-accent-400" />
            <span>Usage This Month</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <div className="text-sm text-neutral-500 dark:text-neutral-400 mb-1">
                Edits Used
              </div>
              <div className="text-2xl font-bold text-neutral-900 dark:text-neutral-50">
                {profile?.edits_used || 0}
              </div>
              <div className="text-sm text-neutral-500 dark:text-neutral-400">
                of {planData?.features?.editsPerMonth === 'unlimited' 
                  ? '∞' 
                  : (profile?.edit_quota || 50)
                } available
              </div>
              {profile?.edits_used > 0 && planData?.features?.editsPerMonth !== 'unlimited' && (
                <div className="mt-2 w-full bg-neutral-200 dark:bg-neutral-700 rounded-full h-2">
                  <div 
                    className="bg-accent-600 h-2 rounded-full"
                    style={{ 
                      width: `${Math.min(100, (profile.edits_used / (profile.edit_quota || 50)) * 100)}%` 
                    }}
                  />
                </div>
              )}
            </div>
            
            <div>
              <div className="text-sm text-neutral-500 dark:text-neutral-400 mb-1">
                Active Sites
              </div>
              <div className="text-2xl font-bold text-neutral-900 dark:text-neutral-50">
                {profile?.site_count || 0}
              </div>
              <div className="text-sm text-neutral-500 dark:text-neutral-400">
                of {planData?.features?.sites === 'unlimited' 
                  ? '∞' 
                  : planData?.features?.sites || 3
                } allowed
              </div>
            </div>
            
            <div>
              <div className="text-sm text-neutral-500 dark:text-neutral-400 mb-1">
                Storage Used
              </div>
              <div className="text-2xl font-bold text-neutral-900 dark:text-neutral-50">
                {profile?.storage_used_mb ? `${profile.storage_used_mb} MB` : '0 MB'}
              </div>
              <div className="text-sm text-neutral-500 dark:text-neutral-400">
                of {planData?.features?.storage || '1GB'} available
              </div>
              {profile?.file_count > 0 && (
                <div className="text-xs text-neutral-400 dark:text-neutral-500 mt-1">
                  {profile.file_count} file{profile.file_count !== 1 ? 's' : ''}
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Available Plans */}
      <Card className="border-neutral-200 dark:border-[#2f2b41]">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Settings className="w-5 h-5 text-accent-600 dark:text-accent-400" />
            <span>Available Plans</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {plans && Object.entries(plans).map(([planId, plan]) => (
              <div 
                key={planId} 
                className={cn(
                  "border rounded-lg p-6",
                  currentPlan === planId 
                    ? "border-accent-500 bg-accent-50 dark:bg-accent-900/20" 
                    : "border-neutral-200 dark:border-[#2f2b41]"
                )}
              >
                <div className="text-center">
                  <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-50 mb-2">
                    {plan.name}
                  </h3>
                  <div className="text-3xl font-bold text-neutral-900 dark:text-neutral-50 mb-1">
                    £{plan.price}
                  </div>
                  <div className="text-sm text-neutral-500 dark:text-neutral-400 mb-6">
                    per month
                  </div>
                  
                  <div className="space-y-3 mb-6 text-left">
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      <span className="text-sm">
                        {plan.features.sites === 'unlimited' ? 'Unlimited' : plan.features.sites} websites
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      <span className="text-sm">
                        {plan.features.editsPerMonth === 'unlimited' 
                          ? 'Unlimited' 
                          : `${plan.features.editsPerMonth}`} edits/month
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      <span className="text-sm">{plan.features.storage} storage</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      <span className="text-sm">{plan.features.support}</span>
                    </div>
                  </div>
                  
                  {currentPlan === planId && isActive ? (
                    <Button className="w-full" variant="outline" disabled>
                      Current Plan
                    </Button>
                  ) : currentPlan === planId && isCancelled ? (
                    <Button
                      onClick={() => handleUpgrade(planId)}
                      disabled={createCheckoutMutation.isLoading}
                      className="w-full bg-green-600 hover:bg-green-700 text-white border-0"
                    >
                      {createCheckoutMutation.isLoading ? (
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <RefreshCw className="w-4 h-4 mr-2" />
                      )}
                      Renew Subscription
                    </Button>
                  ) : (
                    <Button
                      onClick={() => handleUpgrade(planId)}
                      disabled={createCheckoutMutation.isLoading}
                      className="w-full"
                    >
                      {createCheckoutMutation.isLoading ? (
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <ExternalLink className="w-4 h-4 mr-2" />
                      )}
                      {currentPlan === 'basic' ? 'Upgrade' : 'Switch Plan'}
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Billing Actions */}
      {isActive && (
        <Card className="border-neutral-200 dark:border-[#2f2b41]">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <CreditCard className="w-5 h-5 text-accent-600 dark:text-accent-400" />
              <span>Billing Actions</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                onClick={handleCancelSubscription}
                disabled={cancelSubscriptionMutation.isLoading}
                variant="outline"
                className="border-red-300 text-red-600 hover:bg-red-50 dark:border-red-600 dark:text-red-400 dark:hover:bg-red-900/20"
              >
                <AlertCircle className="w-4 h-4 mr-2" />
                {cancelSubscriptionMutation.isLoading ? 'Cancelling...' : 'Cancel Subscription'}
              </Button>
            </div>
            <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-2">
              Note: Subscription changes will take effect at the next billing cycle.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Upgrade Plan Modal */}
      <AnimatePresence>
        {showUpgradeModal && selectedPlan && (
          <UpgradeModal 
            plan={selectedPlan}
            currentPlan={currentPlan}
            isCancelled={isCancelled}
            onClose={() => setShowUpgradeModal(false)}
            onConfirm={confirmUpgrade}
            isLoading={createCheckoutMutation.isLoading}
          />
        )}
      </AnimatePresence>

      {/* Cancel Subscription Modal */}
      <AnimatePresence>
        {showCancelModal && (
          <CancelSubscriptionModal
            onClose={() => setShowCancelModal(false)}
            onConfirm={confirmCancel}
            isLoading={cancelSubscriptionMutation.isLoading}
            currentPlan={planData}
          />
        )}
      </AnimatePresence>
    </div>
  )
}

// Upgrade Modal Component
const UpgradeModal = ({ plan, currentPlan, isCancelled, onClose, onConfirm, isLoading }) => {
  const isUpgrade = currentPlan === 'basic'
  const isRenewal = isCancelled && currentPlan === plan.id
  
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20">
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/60" 
          onClick={onClose} 
        />

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="relative w-full max-w-md"
        >
          <Card className="border-0 shadow-2xl bg-white dark:bg-[#1a1625]">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-accent-500 to-accent-600 rounded-xl flex items-center justify-center">
                    {isUpgrade ? <Zap className="w-5 h-5 text-white" /> : <Crown className="w-5 h-5 text-white" />}
                  </div>
                  <CardTitle className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
                    {isRenewal ? 'Renew Subscription' : isUpgrade ? 'Upgrade Plan' : 'Switch Plan'}
                  </CardTitle>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onClose}
                  className="text-neutral-400 hover:text-neutral-600"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>

            <CardContent className="space-y-6">
              <div className="text-center">
                <p className="text-neutral-700 dark:text-neutral-300 mb-4">
                  {isRenewal ? 'Ready to reactivate your subscription?' : isUpgrade ? 'Ready to unlock more features?' : 'Switch to a different plan?'}
                </p>
                
                <div className="bg-gradient-to-r from-accent-50 to-accent-100 dark:from-accent-900/20 dark:to-accent-800/20 border border-accent-200 dark:border-accent-800 rounded-lg p-6 mb-6">
                  <div className="text-center">
                    <h3 className="text-2xl font-bold text-accent-900 dark:text-accent-100 mb-2">
                      {plan.name}
                    </h3>
                    <div className="text-3xl font-bold text-accent-600 dark:text-accent-400 mb-4">
                      £{plan.price}/month
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex items-center justify-center gap-2">
                        <CheckCircle className="w-4 h-4 text-accent-600" />
                        <span className="text-sm text-accent-700 dark:text-accent-300">
                          {plan.features.sites === 'unlimited' ? 'Unlimited' : plan.features.sites} websites
                        </span>
                      </div>
                      <div className="flex items-center justify-center gap-2">
                        <CheckCircle className="w-4 h-4 text-accent-600" />
                        <span className="text-sm text-accent-700 dark:text-accent-300">
                          {plan.features.editsPerMonth === 'unlimited' 
                            ? 'Unlimited' 
                            : `${plan.features.editsPerMonth}`} edits/month
                        </span>
                      </div>
                      <div className="flex items-center justify-center gap-2">
                        <CheckCircle className="w-4 h-4 text-accent-600" />
                        <span className="text-sm text-accent-700 dark:text-accent-300">
                          {plan.features.storage} storage
                        </span>
                      </div>
                      <div className="flex items-center justify-center gap-2">
                        <CheckCircle className="w-4 h-4 text-accent-600" />
                        <span className="text-sm text-accent-700 dark:text-accent-300">
                          {plan.features.support}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <p className="text-sm text-blue-700 dark:text-blue-300">
                    You'll be redirected to Stripe to complete your {isRenewal ? 'subscription renewal' : isUpgrade ? 'upgrade' : 'plan change'}. 
                    Your billing will be adjusted automatically.
                  </p>
                </div>
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={onClose}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={onConfirm}
                  disabled={isLoading}
                  className="flex-1 bg-gradient-to-r from-accent-600 to-accent-500 hover:from-accent-700 hover:to-accent-600 text-white border-0"
                >
                  {isLoading ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <ExternalLink className="w-4 h-4 mr-2" />
                  )}
                  {isLoading ? 'Processing...' : (isRenewal ? 'Renew Now' : isUpgrade ? 'Upgrade Now' : 'Switch Plan')}
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  )
}

// Cancel Subscription Modal Component
const CancelSubscriptionModal = ({ onClose, onConfirm, isLoading, currentPlan }) => {
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20">
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/60" 
          onClick={onClose} 
        />

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="relative w-full max-w-md"
        >
          <Card className="border-0 shadow-2xl bg-white dark:bg-[#1a1625]">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-red-100 dark:bg-red-900/20 rounded-xl flex items-center justify-center">
                    <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
                  </div>
                  <CardTitle className="text-lg font-semibold text-red-900 dark:text-red-100">Cancel Subscription</CardTitle>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onClose}
                  className="text-neutral-400 hover:text-neutral-600"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>

            <CardContent className="space-y-6">
              <div className="text-center">
                <p className="text-neutral-700 dark:text-neutral-300 mb-4">
                  Are you sure you want to cancel your subscription?
                </p>
                
                <div className="bg-neutral-50 dark:bg-[#201d2e] border border-neutral-200 dark:border-[#2f2b41] rounded-lg p-4 mb-6">
                  <div className="flex items-center justify-center gap-3 mb-3">
                    <Package className="w-5 h-5 text-neutral-600 dark:text-neutral-400" />
                    <span className="font-medium text-neutral-900 dark:text-white">
                      {currentPlan?.name || 'Current Plan'}
                    </span>
                  </div>
                  <div className="text-sm text-neutral-600 dark:text-neutral-400">
                    £{currentPlan?.price || '24.99'}/month
                  </div>
                </div>

                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 space-y-3">
                  <p className="text-sm text-red-700 dark:text-red-300 font-medium">
                    What happens when you cancel:
                  </p>
                  <div className="space-y-2 text-left">
                    <div className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 bg-red-500 rounded-full mt-2"></div>
                      <span className="text-sm text-red-700 dark:text-red-300">
                        Your subscription will remain active until the end of your current billing period
                      </span>
                    </div>
                    <div className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 bg-red-500 rounded-full mt-2"></div>
                      <span className="text-sm text-red-700 dark:text-red-300">
                        You'll lose access to premium features after your subscription ends
                      </span>
                    </div>
                    <div className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 bg-red-500 rounded-full mt-2"></div>
                      <span className="text-sm text-red-700 dark:text-red-300">
                        Your sites and data will be preserved but with limited functionality
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={onClose}
                  className="flex-1"
                >
                  Keep Subscription
                </Button>
                <Button
                  onClick={onConfirm}
                  disabled={isLoading}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white border-0"
                >
                  {isLoading ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <AlertCircle className="w-4 h-4 mr-2" />
                  )}
                  {isLoading ? 'Cancelling...' : 'Cancel Subscription'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  )
}

export default ClientBilling
